import { cached } from '@glimmer/tracking';
import { addDays } from 'date-fns/addDays';
import type Route from '@ember/routing/route';
import type Transition from '@ember/routing/transition';
import { type Registry as Services, service } from '@ember/service';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import type CalendarEventController from 'uplisting-frontend/pods/calendar/event/controller';
import type MonthlyCalendarController from 'uplisting-frontend/pods/monthly/controller';
import CalendarBulkBaseRoute from 'uplisting-frontend/pods/xcalendar/bulk-base-route';
import type { IEventOperation } from 'uplisting-frontend/services/calendar/interaction';
import { localDayToUtcMidnight } from 'uplisting-frontend/utils/date';
import { RowOption } from 'uplisting-frontend/pods/xcalendar/controller';
import PropertyModel from 'uplisting-frontend/models/property';
import { routerHelper } from 'uplisting-frontend/utils/router';
import type MonthlyEventBaseController from 'uplisting-frontend/pods/monthly/event/base-controller';

export default abstract class MonthlyEventBaseRoute<
  T extends MonthlyEventBaseController,
  K extends Route,
> extends CalendarBulkBaseRoute {
  @service('calendar/manager')
  calendarManager!: Services['calendar/manager'];

  @cached
  get eventOperation(): IEventOperation {
    return this.calendarManager.interaction.eventOperation as IEventOperation;
  }

  @cached
  get baseRoute(): string {
    if (routerHelper.targetRoute?.includes('monthly')) {
      return 'monthly';
    }

    return 'calendar.event';
  }

  @cached
  get relatedRate(): ModelRegistry['rate'] | undefined {
    const controller = this.controllerFor(this.baseRoute) as
      | CalendarEventController
      | MonthlyCalendarController;

    return this.calendarManager.data.getCacheFor(
      RowOption.rates,
      this.eventOperation.fromDate,
      controller.property.id,
    ) as ModelRegistry['rate'] | undefined;
  }

  @cached
  get baseParams() {
    const { fromDate, toDate, location } = this.eventOperation;

    const property =
      location instanceof PropertyModel ? location : location.property;

    return {
      from: localDayToUtcMidnight(fromDate),
      to: localDayToUtcMidnight(addDays(toDate, 1)),
      properties: [property],
    };
  }

  setupController(controller: T, model: K, transition: Transition): void {
    super.setupController(controller, model, transition);

    Object.assign(controller, model);
  }

  resetController(
    controller: T,
    isExiting: boolean,
    transition: Transition,
  ): void {
    super.resetController(controller, isExiting, transition);

    controller.showConfirmModal = false;
  }
}
